// Elegir creaciones ya generadas para sumarlas —
// ADR-2026-09-15-un-solo-selector-de-creaciones.
//
// Uno solo para las dos pantallas: la ficha de un producto y el editor de un
// post del calendario. Estuvo duplicado un rato y las copias ya habían empezado
// a divergir —una agrupaba "de este producto", la otra dejaba pasar videos, y el
// botón de confirmar estaba en lugares distintos y roto de maneras distintas—,
// que es exactamente lo que pasa cuando algo se copia en vez de compartirse.
//
// Lo que cambia entre las dos es data, no forma: `varianteIds` (con qué producto
// agrupar, vacío = no agrupar) y `conVideo` (si el destino recibe video).

import { useEffect, useState } from 'react';
import { Check, Loader2, Play } from 'lucide-react';
import CachedImg from '../../components/CachedImg';
import { SheetLateral } from '../../components/SheetLateral';
import { fetchGeneradasParaElegir, type GeneradaParaElegir } from './lanzamientosApi';

export function SelectorDeCreaciones({
  abierta,
  companyId,
  varianteIds = [],
  yaUsadas,
  conVideo = false,
  cupo = Infinity,
  onCerrar,
  onConfirmar,
}: {
  abierta: boolean;
  companyId: string | null;
  /** Variantes del producto abierto: sus creaciones van primero. Vacío = sin agrupar. */
  varianteIds?: string[];
  /** Los ids de `imagenes_generadas` que el destino YA tiene: no se ofrecen. */
  yaUsadas: string[];
  /** El destino recibe video (`reglasDe(canal).conVideo`). */
  conVideo?: boolean;
  /**
   * Cuántas más entran. Es el hueco que QUEDA, no el tope total.
   *
   * Sin esto, un post vacío podía elegir once creaciones de una y el carrusel
   * se guardaba pasado de diez: la pantalla lo avisaba en ámbar recién después,
   * y sacar piezas ya cargadas es más trabajo que no dejarlas entrar
   * (ADR-2026-09-16-el-selector-conoce-el-hueco-que-queda). Sin tope por
   * default: la ficha de un producto no tiene ninguno.
   */
  cupo?: number;
  onCerrar: () => void;
  onConfirmar: (elegidas: GeneradaParaElegir[]) => Promise<void>;
}) {
  const [imagenes, setImagenes] = useState<GeneradaParaElegir[]>([]);
  const [cargando, setCargando] = useState(false);
  const [elegidas, setElegidas] = useState<string[]>([]);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!abierta || !companyId) return;
    let vivo = true;
    setElegidas([]);
    void (async () => {
      setCargando(true);
      const r = await fetchGeneradasParaElegir(companyId, varianteIds, yaUsadas, conVideo);
      if (!vivo) return;
      setImagenes(r);
      setCargando(false);
    })();
    return () => {
      vivo = false;
    };
    // `varianteIds` y `yaUsadas` son arrays nuevos en cada pintado: si entraran,
    // el efecto se dispararía solo para siempre. Lo que de verdad reabre la
    // lista es abrir el sheet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierta, companyId, conVideo]);

  /** No entra ninguna más: las no elegidas se apagan en vez de sumar de más. */
  const lleno = elegidas.length >= cupo;

  const delProducto = imagenes.filter((i) => i.delProducto);
  const resto = imagenes.filter((i) => !i.delProducto);

  const grilla = (lista: GeneradaParaElegir[]) => (
    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
      {lista.map((img) => {
        const puesta = elegidas.includes(img.id);
        const cerrada = lleno && !puesta;
        return (
          <button
            key={img.id}
            type="button"
            disabled={cerrada}
            title={cerrada ? `No entran más de ${cupo}` : undefined}
            onClick={() =>
              setElegidas((prev) =>
                prev.includes(img.id) ? prev.filter((x) => x !== img.id) : [...prev, img.id],
              )
            }
            className={`relative aspect-square overflow-hidden rounded-xl ${
              puesta ? 'ring-2 ring-brand-primary' : 'ring-1 ring-neutral-200'
            } ${cerrada ? 'opacity-40' : ''}`}
          >
            {img.thumbUrl ? (
              <CachedImg src={img.thumbUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              // Un video sin póster: mejor el ícono que un cuadro roto.
              <span className="grid h-full w-full place-items-center bg-neutral-100">
                <Play className="w-5 h-5 text-neutral-400" />
              </span>
            )}
            {img.esVideo && img.thumbUrl && (
              <span className="absolute left-1.5 top-1.5 grid h-5 w-5 place-items-center rounded-full bg-black text-white">
                <Play className="w-3 h-3" />
              </span>
            )}
            {puesta && (
              <span className="absolute right-1.5 top-1.5 grid h-5 w-5 place-items-center rounded-full bg-brand-primary text-white">
                <Check className="w-3 h-3" />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );

  const queSon = conVideo ? 'creación' : 'foto';

  return (
    <SheetLateral
      open={abierta}
      onClose={onCerrar}
      titulo="Elegir una creación"
      // El pie va en la ranura del sheet, no adentro del contenido: ahí queda
      // pegado al borde de abajo y se ve sin scrollear.
      pie={
        elegidas.length > 0 ? (
          <button
            type="button"
            disabled={guardando}
            onClick={() =>
              void (async () => {
                setGuardando(true);
                await onConfirmar(imagenes.filter((i) => elegidas.includes(i.id)));
                setGuardando(false);
              })()
            }
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-black py-3 px-6 text-sm font-medium text-white disabled:opacity-40"
          >
            {guardando && <Loader2 className="w-4 h-4 animate-spin" />}
            Sumar {elegidas.length} {queSon}
            {elegidas.length === 1 ? '' : conVideo ? 'es' : 's'}
          </button>
        ) : undefined
      }
    >
      {cargando ? (
        <div className="flex items-center justify-center gap-2 py-12 text-sm text-neutral-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Buscando tus creaciones…
        </div>
      ) : imagenes.length === 0 ? (
        <p className="py-12 text-center text-sm text-neutral-500">
          No hay creaciones para sumar. Las que ya están no se repiten.
        </p>
      ) : (
        <div className="space-y-5">
          {lleno && Number.isFinite(cupo) && (
            <p className="rounded-xl bg-amber-50 py-2 px-3 text-xs text-amber-800">
              Ya elegiste {cupo}, que es todo lo que entra. Sacá alguna para cambiarla.
            </p>
          )}
          {delProducto.length > 0 && (
            <div>
              <p className="text-xs font-medium text-neutral-500">De este producto</p>
              <div className="mt-2">{grilla(delProducto)}</div>
            </div>
          )}
          {resto.length > 0 && (
            <div>
              <p className="text-xs font-medium text-neutral-500">
                {delProducto.length > 0 ? 'Otras creaciones' : 'Tus creaciones'}
              </p>
              <div className="mt-2">{grilla(resto)}</div>
            </div>
          )}
        </div>
      )}
    </SheetLateral>
  );
}

export default SelectorDeCreaciones;
